import React, {
  useEffect, memo, useState, useMemo, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { offerQualifyBuilder, getSessionData } from '../../offerUtils.js';
import { setIsMatchedAACrossOffer } from '../../offerSlice.js';
import {
  useOptOfferMutation,
  useQualifyOfferMutation,
} from '../../../../../store/services/common/offers/offerApi.js';
import { useLogTrackMutation } from '../../../../../store/services/common/logTrack/logTrackApi';
import pubSubInstance from '../../../../../shared/utils/pubSub.js';
import { showLoadder, hideLoadder } from '../../../../../store/slices/loadderSlice';

const ActiveAdvantageCrossSell = () => {
  const dispatch = useDispatch();
  const {
    entities: {
      AACrossToken: widgetToken,
      offerInitData: {
        widgetAPIServerHost,
      } = {},
    },
    isMatchedAACrossOffer,
  } = useSelector(state => state.offersData);

  const {
    entities: {
      orderInfo: {
        currencyCode,
        activeAdvantageSavingAmount: savingAmount,
      },
    },
  } = useSelector(state => state.mainStateData);

  const {
    widgetNameSpace,
    locale,
  } = useSelector(state => state.propsData);

  const [ optOffer ] = useOptOfferMutation();
  const [ qualifyOffer ] = useQualifyOfferMutation();
  const [ logTrack ] = useLogTrackMutation();

  const offerRenderer = window.COMMERCEWidget?.offerRenderer;

  const aaData = getSessionData(`${widgetNameSpace}-checkoutAACross`);
  const defaultValue = aaData.accept;
  const [ keepAAOffer, setKeepAAOffer ] = useState(!isEmpty(aaData) && defaultValue);
  const [ optionValue, setOptionValue ] = useState(defaultValue);
  const [ optDataState, setOptDataState ] = useState(undefined);
  const [ renderAACross, setRenderAACross ] = useState(null);

  const handleOptDataChanged = useCallback((optData) => {
    const { accept, status, response: { products, quoteId } } = optData;

    const storeData = {
      accept,
      status,
      quoteId,
      products,
      AACrossToken: widgetToken,
    };

    if (keepAAOffer) {
      setKeepAAOffer(false);
    } else {
      if (accept === optionValue) return;
      sessionStorage.setItem(`${widgetNameSpace}-checkoutAACross`, JSON.stringify(storeData));

      optOffer({
        offerType: 'aa-cross',
        params: {
          opted: accept,
          products,
        },
      }).unwrap().then((
        { repriceRequired },
      ) => {
        if (repriceRequired) {
          pubSubInstance.dispatch(`${widgetNameSpace}-reload`);
        }
      });
      setOptionValue(accept);
    }
  }, [ keepAAOffer, optionValue, optOffer, setKeepAAOffer, setOptionValue ]);

  const handleAACrossOptionChange = useCallback((data) => {
    if (isEqual(data, optDataState)) return;
    setOptDataState(data);
  }, [ optDataState, setOptDataState ]);

  const qualifyAACrossOffer = useMemo(() => {
    return offerQualifyBuilder({
      qualifyOffer, offerType: 'aa-cross', offer: renderAACross, logTrack,
    });
  }, [ qualifyOffer, renderAACross, logTrack ]);

  const updateAmount = useCallback(({ amount, callback }) => {
    if (isMatchedAACrossOffer) {
      renderAACross.CommerceObserver.emit('updateAmount', { params: { amount }, callback });
    }
  }, [ renderAACross, isMatchedAACrossOffer ]);

  const mountAACross = () => {
    if (!widgetToken || !offerRenderer || isNil(savingAmount)) {
      return null;
    }
    const AACrossData = getSessionData(`${widgetNameSpace}-checkoutAACross`);
    sessionStorage.setItem(`${widgetNameSpace}-checkoutAACross`, JSON.stringify({ ...AACrossData, AACrossToken: widgetToken }));
    const { accept: defaultAccept } = AACrossData;

    const params = {
      defaultAccept,
      jwt: widgetToken,
      locale,
      widgetAPIServerHost,
      savePriceAmount: savingAmount,
      savePriceCode: currencyCode,
      handleChange: handleAACrossOptionChange,
      initCallback: ({ response: { matchedOffer } }) => {
        dispatch(setIsMatchedAACrossOffer(matchedOffer));
      },
      show: () => {
        dispatch(showLoadder());
      },
      hide: () => {
        dispatch(hideLoadder());
      },
    };
    const prop = omitBy(params, isUndefined);

    renderAACross?.destroy();// For props are from async api, So it should rerender the widget
    return offerRenderer.renderActiveAdvantageCrossSell(
      document.getElementById('offer-widget-AACross'), prop);
  };

  const handleQualify = useCallback((paymentData) => {
    return new Promise((resolve) => {
      const { accept, response: { products, quoteId } = {} } = optDataState || {};
      if (!isMatchedAACrossOffer || !accept) {
        resolve({ status: true });
      } else {
        qualifyAACrossOffer({
          params: {
            ...paymentData,
            products,
          },
          quoteId,
        }).then(({ status }) => {
          if (status) {
            resolve({ status: true });
          } else {
            resolve({ status: false });
          }
        });
      }
    });
  }, [ optDataState, qualifyAACrossOffer, isMatchedAACrossOffer, widgetNameSpace ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-offer-qualify`, handleQualify);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-offer-qualify`, handleQualify);
    };
  }, [ widgetNameSpace, handleQualify ]);

  useEffect(() => {
    !isEmpty(optDataState) && handleOptDataChanged(optDataState);
  }, [ optDataState ]);

  useEffect(() => {
    const renderer = mountAACross();
    setRenderAACross(renderer);
    return () => {
      renderer?.destroy();
    };
  }, [ widgetToken, offerRenderer, widgetAPIServerHost, locale ]);

  useEffect(() => {
    !isNil(savingAmount) && updateAmount({
      amount: savingAmount,
    });
  }, [ savingAmount ]);

  if (!widgetToken || !offerRenderer || isNil(savingAmount)) {
    return null;
  }

  return (
    <div className="active-advantage-cross-sell-offer" id="offer-widget-AACross" />
  );
};

export default memo(ActiveAdvantageCrossSell);
